import React, {useCallback, useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import moment from "moment";
import {useTranslation} from "react-i18next";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import VLFCard from "@/components/Card";
import ViewField from "@/components/Input/ViewField";
import VLFModal from "@/components/Modal";
import ShotTargetMap from "@/components/ShotTargetMap";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PrintIcon from "@mui/icons-material/Print";
import Tooltip from "@mui/material/Tooltip";

import {MatchReportAPI} from "@/api/match_report";

import {computeGoalkeeperMatchMetrics} from "@/utils/goalkeeperMatchMetrics";
import {GOALKEEPER_KPI_CONFIG} from "@/utils/goalkeeperKpiConfig";
import {generateGoalkeeperMatchReportPDF} from "@/utils/generateGoalkeeperMatchReportPDF";

import {
	dateFormat,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";

import "./index.scss";

function Valuation(props) {
	const {handleNotification} = props;
	const {t} = useTranslation();
	const naPlaceholder = t("goalkeeper_report.common.numeric_placeholder");

	const {id} = useParams();
	const navigate = useNavigate();

	const [loading, setLoading] = useState(false);
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [reportData, setReportData] = useState({});
	const shotMapAreaRef = useRef(null);

	const getReport = useCallback(async () => {
		setLoading(true);
		try {
			const response = await MatchReportAPI.getById(id);
			setReportData(response);
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	}, [id, handleNotification]);

	useEffect(() => {
		getReport();
	}, [getReport]);

	const deleteReport = async () => {
		setLoading(true);
		try {
			await MatchReportAPI.delete(reportData?.id);
			handleNotification(SUCCESSFUL_OPERATION, "success");
			navigate("/match-reports");
		} catch (error) {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const exportReport = async () => {
		try {
			const shotMapGoalElement =
				shotMapAreaRef.current?.querySelector(".stm__goal") || null;

			await generateGoalkeeperMatchReportPDF({
				reportData,
				t,
				shotMapGoalElement,
				fileNamePrefix: t("goalkeeper_report.export_title"),
			});

			handleNotification("PDF generato con successo", "success");
		} catch (error) {
			console.error("Errore nella generazione del PDF:", error);
			handleNotification(
				error?.message || "Errore nella generazione del PDF",
				"error",
			);
		}
	};

	const getConcededGoalDescriptions = () => {
		const descriptions = (
			reportData?.goalDescriptions ??
			[1, 2, 3, 4, 5, 6].map((n) => reportData?.["descriptionGoalConceded" + n])
		).filter((text) => text && text.length);

		return descriptions;
	};

	const concededGoalDescriptions = getConcededGoalDescriptions();

	// -----------------------------
	// Derived goalkeeper analytics (read-only UI)
	// -----------------------------
	const KPI_CONFIG = GOALKEEPER_KPI_CONFIG;

	const {
		shotsOnTargetPoints,
		shotsOnTargetCount,
		hasPerShotOutcomes,

		goalsConcededValue,
		finalRatingValue,

		savesOnTargetValue,
		shotStoppingIndexRatio,
		distributionEfficiencyRatio,
		aerialControlRatio,
		setPieceControlRatio,
		errorImpactRatio,
	} = computeGoalkeeperMatchMetrics(reportData);

	const getKpiStatusFromConfig = (kpiKey, rawValue) => {
		const config = KPI_CONFIG[kpiKey];
		if (!config) return "unavailable";
		if (rawValue === null) return "unavailable";

		if (config.type === "score10") {
			if (rawValue >= config.thresholds.good) return "green";
			if (rawValue >= config.thresholds.avg) return "yellow";
			return "red";
		}

		if (config.type === "ratio") {
			if (rawValue >= config.thresholds.good) return "green";
			if (rawValue >= config.thresholds.avg) return "yellow";
			return "red";
		}

		// ratioLowerIsBetter
		if (rawValue <= config.thresholds.good) return "green";
		if (rawValue <= config.thresholds.avg) return "yellow";
		return "red";
	};

	const getKpiInterpretationFromConfig = (kpiKey, rawValue) => {
		const config = KPI_CONFIG[kpiKey];
		if (!config) return naPlaceholder;
		if (rawValue === null) return t("goalkeeper_report.not_available");

		const status = getKpiStatusFromConfig(kpiKey, rawValue);
		const semantic =
			status === "green" ? "good" : status === "yellow" ? "avg" : "poor";
		return t(`goalkeeper_report.kpi_interpretation.${kpiKey}.${semantic}`);
	};

	const formatOptionalRatioPct = (ratioValue, decimals = 0) => {
		if (ratioValue === null) return "---";
		return `${(ratioValue * 100).toFixed(decimals)}%`;
	};

	const finalRatingDisplay =
		finalRatingValue !== null ? finalRatingValue.toFixed(2) : naPlaceholder;

	const finalRatingStatus = getKpiStatusFromConfig(
		"finalRating",
		finalRatingValue,
	);

	const headerReportDate = reportData?.reportDate
		? moment(reportData?.reportDate).format(dateFormat)
		: "---";
	const headerCoachName = reportData?.reportAuthor || "---";
	const headerGoalkeeperName = reportData?.player
		? `${reportData?.player?.firstName ?? ""} ${reportData?.player?.lastName ?? ""} (${reportData?.player?.category ?? ""})`
		: "---";
	const headerMatchReference = reportData?.match
		? `${moment(reportData?.match?.startDate).format(dateFormat)} - ${reportData?.match?.homeClub} - ${reportData?.match?.awayClub}`
		: "---";

	// KPI displays and statuses (for the compact summary cards).
	const shotStoppingIndexDisplay = formatOptionalRatioPct(
		shotStoppingIndexRatio,
		0,
	);
	const distributionEfficiencyDisplay = formatOptionalRatioPct(
		distributionEfficiencyRatio,
		0,
	);
	const aerialControlDisplay = formatOptionalRatioPct(aerialControlRatio, 0);
	const setPieceControlDisplay = formatOptionalRatioPct(
		setPieceControlRatio,
		0,
	);
	const errorImpactDisplay = formatOptionalRatioPct(errorImpactRatio, 0);

	const shotStoppingIndexStatus = getKpiStatusFromConfig(
		"shotStopping",
		shotStoppingIndexRatio,
	);
	const distributionEfficiencyStatus = getKpiStatusFromConfig(
		"distribution",
		distributionEfficiencyRatio,
	);
	const aerialControlStatus = getKpiStatusFromConfig(
		"aerialControl",
		aerialControlRatio,
	);
	const setPieceControlStatus = getKpiStatusFromConfig(
		"setPieces",
		setPieceControlRatio,
	);
	const errorImpactStatus = getKpiStatusFromConfig(
		"errorImpact",
		errorImpactRatio,
	);

	const shotStoppingInterpretation = getKpiInterpretationFromConfig(
		"shotStopping",
		shotStoppingIndexRatio,
	);
	const distributionInterpretation = getKpiInterpretationFromConfig(
		"distribution",
		distributionEfficiencyRatio,
	);
	const aerialInterpretation = getKpiInterpretationFromConfig(
		"aerialControl",
		aerialControlRatio,
	);
	const setPiecesInterpretation = getKpiInterpretationFromConfig(
		"setPieces",
		setPieceControlRatio,
	);
	const errorImpactInterpretation = getKpiInterpretationFromConfig(
		"errorImpact",
		errorImpactRatio,
	);
	const finalRatingInterpretation = getKpiInterpretationFromConfig(
		"finalRating",
		finalRatingValue,
	);

	// Shot map quick interpretation (simple coaching-oriented rule).
	// Formula: use save% thresholds to interpret overall effectiveness on shots.
	const shotMapInsight =
		shotStoppingIndexRatio === null
			? t("goalkeeper_report.shot_map_insight.unavailable")
			: shotStoppingIndexRatio >= KPI_CONFIG.shotStopping.thresholds.good
				? t("goalkeeper_report.shot_map_insight.high_efficiency")
				: shotStoppingIndexRatio >= KPI_CONFIG.shotStopping.thresholds.avg
					? t("goalkeeper_report.shot_map_insight.avg_performance")
					: t("goalkeeper_report.shot_map_insight.insufficient");

	// Match summary insight: combine rating + shot stopping + error impact (simple, no AI).
	const matchSummaryInsight = (() => {
		const ratingStatus = finalRatingStatus;
		const stoppingStatus = shotStoppingIndexStatus;
		const errorStatus = errorImpactStatus;

		const parts = [];

		if (ratingStatus === "green")
			parts.push(t("goalkeeper_report.match_summary.rating.good"));
		else if (ratingStatus === "yellow")
			parts.push(t("goalkeeper_report.match_summary.rating.avg"));
		else if (ratingStatus === "red")
			parts.push(t("goalkeeper_report.match_summary.rating.poor"));
		else parts.push(t("goalkeeper_report.match_summary.rating.unavailable"));

		if (stoppingStatus === "green")
			parts.push(t("goalkeeper_report.match_summary.stopping.good"));
		else if (stoppingStatus === "yellow")
			parts.push(t("goalkeeper_report.match_summary.stopping.avg"));
		else if (stoppingStatus === "red")
			parts.push(t("goalkeeper_report.match_summary.stopping.poor"));

		if (errorStatus === "green")
			parts.push(t("goalkeeper_report.match_summary.error.good"));
		else if (errorStatus === "yellow")
			parts.push(t("goalkeeper_report.match_summary.error.avg"));
		else if (errorStatus === "red")
			parts.push(t("goalkeeper_report.match_summary.error.poor"));

		return parts.join(" ");
	})();

	const shotsOnTargetFacedDisplay =
		shotsOnTargetCount > 0 ? shotsOnTargetCount : naPlaceholder;
	const savesDisplay =
		savesOnTargetValue !== null ? savesOnTargetValue : naPlaceholder;
	const goalsConcededDisplay =
		goalsConcededValue !== null ? goalsConcededValue : naPlaceholder;
	const savePercentageDisplay = shotStoppingIndexDisplay;

	const reportConfigurationSize = "col-12 col-sm-6 col-lg-4";
	const reportConfiguration = [
		{
			value: reportData?.generalOpinion,
			label: t("goalkeeper_report.view_fields.general_opinion"),
			size: reportConfigurationSize,
		},
		{
			value: (
				<>
					{concededGoalDescriptions?.length ? (
						<ol className="coach-notes-goals-list">
							{concededGoalDescriptions.map((text, idx) => (
								<li key={idx} className="coach-notes-goal-item">
									{text}
								</li>
							))}
						</ol>
					) : (
						<div className="coach-notes-empty-state">
							{t("goalkeeper_report.report_info.no_conceded_goals_description")}
						</div>
					)}
				</>
			),
			label: t("goalkeeper_report.report_info.conceded_goals_description"),
			size: "col-6",
		},
	];

	const matchReportConfiguration = [
		{
			value: reportData?.heatingExecution,
			label: t("goalkeeper_report.view_fields.warmup_execution"),
			size: reportConfigurationSize,
		},
		{
			value:
				reportData?.minutesPlayed != null
					? reportData?.minutesPlayed + "'"
					: "---",
			label: t("goalkeeper_report.view_fields.minutes_played"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.totalGoalsConceded,
			label: t("goalkeeper_report.view_fields.goals_conceded"),
			size: reportConfigurationSize,
		},
		{
			value: (
				<div className="d-flex flex-column">
					{reportData?.replaced == null ? (
						<span>Non raccolto</span>
					) : (
						<div className={"cell" + (reportData.replaced ? " on" : " off")} />
					)}
					{reportData?.reasonReplaced ? (
						<div className="match-report__reason">
							{reportData?.reasonReplaced}
						</div>
					) : (
						""
					)}
				</div>
			),
			label: t("goalkeeper_report.view_fields.replaced"),
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: (
				<div className="d-flex flex-column">
					{reportData?.warned == null ? (
						<span>Non raccolto</span>
					) : (
						<div className={"cell" + (reportData.warned ? " on" : " off")} />
					)}
					{reportData?.reasonWarned ? (
						<div className="match-report__reason">
							{reportData?.reasonWarned}
						</div>
					) : (
						""
					)}
				</div>
			),
			label: t("goalkeeper_report.view_fields.warned"),
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: (
				<div className="d-flex flex-column">
					{reportData?.expelled == null ? (
						<span>Non raccolto</span>
					) : (
						<div className={"cell" + (reportData.expelled ? " on" : " off")} />
					)}
					{reportData?.reasonExpelled ? (
						<div className="match-report__reason">
							{reportData?.reasonExpelled}
						</div>
					) : (
						""
					)}
				</div>
			),
			label: t("goalkeeper_report.view_fields.expelled"),
			size: "col-12 col-sm-6 col-lg-4",
		},
		{
			value: reportData?.note,
			label: t("goalkeeper_report.view_fields.note"),
			size: "col-12 col-sm-6 col-lg-4",
		},
	];

	const cornersInConfiguration = [
		{
			value: reportData?.cornerRetIntervention,
			label: t("goalkeeper_report.view_fields.corner_in_goalkeeper_exit"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerRetSave,
			label: t("goalkeeper_report.view_fields.corner_in_save_direct"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerRetDefendedTeammate,
			label: t("goalkeeper_report.view_fields.corner_in_defended_by_teammates"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerRetOpponents,
			label: t(
				"goalkeeper_report.view_fields.corner_in_shot_hit_by_opponents_no_goal",
			),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerRetGoal,
			label: t("goalkeeper_report.view_fields.corner_in_goal_direct"),
			size: reportConfigurationSize,
		},
	];

	const cornersOutConfiguration = [
		{
			value: reportData?.cornerOutIntervention,
			label: t("goalkeeper_report.view_fields.corner_out_goalkeeper_exit"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerOutSave,
			label: t("goalkeeper_report.view_fields.corner_out_save_direct"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerOutDefendedTeammate,
			label: t(
				"goalkeeper_report.view_fields.corner_out_defended_by_teammates",
			),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerOutOpponents,
			label: t(
				"goalkeeper_report.view_fields.corner_out_shot_hit_by_opponents_no_goal",
			),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.cornerOutGoal,
			label: t("goalkeeper_report.view_fields.corner_out_goal_direct"),
			size: reportConfigurationSize,
		},
	];

	const statsConfiguration = [
		{
			value: reportData?.manToManMarking,
			label: t("goalkeeper_report.view_fields.corner_metrics_man_to_man"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.zonalMarking,
			label: t("goalkeeper_report.view_fields.corner_metrics_zone"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.mixedMarking,
			label: t("goalkeeper_report.view_fields.corner_metrics_mixed"),
			size: reportConfigurationSize,
		},

		{
			value: reportData?.blockedCorners,
			label: t("goalkeeper_report.view_fields.corner_metrics_blocked"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.shortCorners,
			label: t("goalkeeper_report.view_fields.corner_metrics_short_taken"),
			size: reportConfigurationSize,
		},
		{
			value: reportData?.totalAttacksArea,
			label: t("goalkeeper_report.view_fields.corner_metrics_area_attacks"),
			size: reportConfigurationSize,
		},
	];

	const getParameterRow = (paramLabel, okLabel, failedLabel) => {
		return (
			<div className="row">
				<div className="col-12 col-lg-6 parameter-container">
					<div className="field-label-container">
						<div className="field-label">{paramLabel}</div>
					</div>
					<div className="parameter-total">
						{parseInt(reportData[okLabel] || 0) +
							parseInt(reportData[failedLabel] || 0)}
					</div>
				</div>
				<div className="col-12 col-md-6 col-lg-3 parameter-container">
					<div className="field-label-container">
						<div className="field-label">
							{t("goalkeeper_report.common.yes")}
						</div>
					</div>
					<div className="parameter-total positive">
						{parseInt(reportData[okLabel] || 0)}
					</div>
				</div>
				<div className="col-12 col-md-6 col-lg-3 parameter-container">
					<div className="field-label-container">
						<div className="field-label">
							{t("goalkeeper_report.common.no")}
						</div>
					</div>
					<div className="parameter-total negative">
						{parseInt(reportData[failedLabel] || 0)}
					</div>
				</div>
			</div>
		);
	};

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: t("back"),
			onClick: () => navigate("/match-reports"),
			color: "info",
		},
		{
			icon: <PrintIcon />,
			title: t("goalkeeper_report.action.export_report"),
			onClick: exportReport,
			color: "info",
		},
		{
			icon: <EditIcon />,
			title: t("goalkeeper_report.action.edit"),
			onClick: () => navigate("/match-reports/" + reportData?.id + "/edit"),
		},
		{
			icon: <DeleteIcon />,
			title: t("goalkeeper_report.action.delete"),
			onClick: () => setOpenHandleConfirm(true),
			color: "error",
		},
	];

	const breadcrumbPlayerName =
		reportData?.player?.firstName || reportData?.player?.lastName
			? `${reportData?.player?.firstName ?? ""} ${reportData?.player?.lastName ?? ""}`.trim()
			: t("player");

	const breadcrumbsElements = [
		{
			label: t("goalkeeper_match_report_form.breadcrumb_list"),
			path: "/match-reports",
		},
		{label: "/"},
		{
			label: breadcrumbPlayerName,
		},
		{label: "/"},
	];

	return (
		<Page className="page-container">
			<Loading visible={loading} />
			{reportData.common && (
				<div className="alert alert-info mt-2">
					<p>
						La mappa e le sue percentuali si riferiscono ai tiri registrati;
						fuori e pali sono esclusi dai tiri in porta.
					</p>
					<p>
						Dati comuni letti dalla partita. RPE:{" "}
						{reportData.common.postMatchRpe ?? "Non raccolto"}.
					</p>
					{reportData.common.warning && <p>{reportData.common.warning}</p>}
					<p>
						La mappa e le sue percentuali si riferiscono ai tiri registrati; i
						tiri fuori o sul palo sono esclusi dai tiri in porta.
					</p>
					<a href={"/matches/" + reportData.matchId}>
						Apri la partita per modificare minuti, voto, RPE e cartellini
					</a>
					{Object.entries(reportData.common.legacyValues ?? {})
						.filter(
							([key, value]) =>
								value != null &&
								String(value) !== String(reportData.common[key]),
						)
						.map(([key, value]) => (
							<p key={key} className="mt-2 mb-0">
								Dato precedente conservato —{" "}
								{
									{
										minutesPlayed: "Minuti",
										generalRating: "Voto",
										postMatchRpe: "RPE",
										replaced: "Sostituito",
										warned: "Ammonito",
										expelled: "Espulso",
									}[key]
								}
								: {typeof value === "boolean" ? (value ? "Sì" : "No") : value}.
								Valore della partita:{" "}
								{reportData.common[key] == null
									? "Non raccolto"
									: typeof reportData.common[key] === "boolean"
										? reportData.common[key]
											? "Sì"
											: "No"
										: reportData.common[key]}
								.
							</p>
						))}
				</div>
			)}

			<Breadcrumbs
				elements={breadcrumbsElements}
				current={headerReportDate}
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="sheet-container">
				<div className="report-summary-container">
					<div className="report-summary-header">
						<div className="report-summary-grid">
							<div className="report-summary-cell">
								<div className="report-summary-label">
									{t("goalkeeper_report.header.date")}
								</div>
								<div className="report-summary-value">{headerReportDate}</div>
							</div>
							<div className="report-summary-cell">
								<div className="report-summary-label">
									{t("goalkeeper_report.header.coach")}
								</div>
								<div className="report-summary-value">{headerCoachName}</div>
							</div>
							<div className="report-summary-cell">
								<div className="report-summary-label">
									{t("goalkeeper_report.header.goalkeeper")}
								</div>
								<div className="report-summary-value">
									{headerGoalkeeperName}
								</div>
							</div>
							<div className="report-summary-cell">
								<div className="report-summary-label">
									{t("goalkeeper_report.header.match")}
								</div>
								<div className="report-summary-value">
									{headerMatchReference}
								</div>
							</div>
						</div>
					</div>

					<div
						className="kpi-summary-row"
						aria-label={t("goalkeeper_report.kpi_summary_aria")}
					>
						<div
							className={
								"kpi-card kpi-card--primary " +
								`kpi-card--${finalRatingStatus}` +
								" compact"
							}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.final_rating")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.final_rating")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">{finalRatingDisplay}</div>
							<div className="kpi-card-sub">{finalRatingInterpretation}</div>
						</div>

						<div
							className={
								"kpi-card kpi-card--primary " +
								`kpi-card--${shotStoppingIndexStatus}` +
								" compact"
							}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.shot_stopping")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.shot_stopping")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">{shotStoppingIndexDisplay}</div>
							<div className="kpi-card-sub">{shotStoppingInterpretation}</div>
						</div>

						<div
							className={
								"kpi-card " +
								`kpi-card--${distributionEfficiencyStatus}` +
								" compact"
							}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.distribution")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.distribution")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">
								{distributionEfficiencyDisplay}
							</div>
							<div className="kpi-card-sub">{distributionInterpretation}</div>
						</div>

						<div
							className={`kpi-card kpi-card--${aerialControlStatus} compact`}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.aerial_control")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.aerial_control")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">{aerialControlDisplay}</div>
							<div className="kpi-card-sub">{aerialInterpretation}</div>
						</div>

						<div
							className={`kpi-card kpi-card--${setPieceControlStatus} compact`}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.set_piece_control")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.set_piece_control")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">{setPieceControlDisplay}</div>
							<div className="kpi-card-sub">{setPiecesInterpretation}</div>
						</div>

						<div
							className={
								"kpi-card kpi-card--primary " +
								`kpi-card--${errorImpactStatus}` +
								" compact"
							}
						>
							<div className="kpi-card-title gk-kpi-title">
								<span>{t("goalkeeper_report.kpi.error_impact")}</span>
								<Tooltip
									title={t("goalkeeper_report.tooltip.error_impact")}
									arrow
									placement="top"
								>
									<InfoOutlinedIcon
										className="gk-kpi-info-icon"
										fontSize="small"
									/>
								</Tooltip>
							</div>
							<div className="kpi-card-value">{errorImpactDisplay}</div>
							<div className="kpi-card-sub">{errorImpactInterpretation}</div>
						</div>
					</div>

					<div
						className="match-summary-insight"
						aria-label={t("goalkeeper_report.match_summary.aria")}
					>
						<div className="match-summary-title">
							{t("goalkeeper_report.match_summary.title")}
						</div>
						<div className="match-summary-text">{matchSummaryInsight}</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.info_match")}
							</div>
							<div className="gk-section-content">
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={matchReportConfiguration} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.info_report")}
							</div>
							<div className="gk-section-content">
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={reportConfiguration} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.shot_map")}
							</div>
							<div className="gk-section-content">
								<div className="row no-margin shot-stopping-content">
									<div className="col-12">
										<div className="shot-map-area" ref={shotMapAreaRef}>
											<div className="shot-map-visual">
												<ShotTargetMap points={shotsOnTargetPoints} readOnly />

												{hasPerShotOutcomes ? (
													<div
														className="shot-map-legend"
														aria-label={t(
															"goalkeeper_report.legend_shot_outcomes",
														)}
													>
														<div className="legend-item">
															<span className="legend-marker legend-marker--save" />
															<span>{t("goalkeeper_report.legend_saved")}</span>
														</div>
														<div className="legend-item">
															<span className="legend-marker legend-marker--goal" />
															<span>{t("goalkeeper_report.legend_goal")}</span>
														</div>
														<div className="legend-item">
															<span className="legend-marker legend-marker--shot" />
															<span>
																{t("goalkeeper_report.legend_shot_unknown")}
															</span>
														</div>
													</div>
												) : (
													<div className="shot-map-legend shot-map-legend--unavailable">
														<div className="legend-item">
															<span className="legend-marker legend-marker--shot" />
															<span>{t("goalkeeper_report.legend_shots")}</span>
														</div>
														<div className="legend-hint">
															{t("goalkeeper_report.legend_hint_unavailable")}
														</div>
													</div>
												)}
											</div>

											<div className="shot-map-side">
												<div
													className="shot-map-summary-line"
													role="group"
													aria-label={t(
														"goalkeeper_report.shot_map_summary.aria",
													)}
												>
													<span className="shot-map-summary-line__item">
														<span className="shot-map-summary-line__label">
															{t(
																"goalkeeper_report.shot_map_summary.abbr_shots_faced",
															)}
														</span>
														<span className="shot-map-summary-line__val">
															{shotsOnTargetFacedDisplay}
														</span>
													</span>
													<span
														className="shot-map-summary-line__sep"
														aria-hidden="true"
													>
														·
													</span>
													<span className="shot-map-summary-line__item">
														<span className="shot-map-summary-line__label">
															{t(
																"goalkeeper_report.shot_map_summary.abbr_saves",
															)}
														</span>
														<span className="shot-map-summary-line__val">
															{savesDisplay}
														</span>
													</span>
													<span
														className="shot-map-summary-line__sep"
														aria-hidden="true"
													>
														·
													</span>
													<span className="shot-map-summary-line__item">
														<span className="shot-map-summary-line__label">
															{t(
																"goalkeeper_report.shot_map_summary.abbr_goals",
															)}
														</span>
														<span className="shot-map-summary-line__val">
															{goalsConcededDisplay}
														</span>
													</span>
													<span
														className="shot-map-summary-line__sep"
														aria-hidden="true"
													>
														·
													</span>
													<span className="shot-map-summary-line__item">
														<span className="shot-map-summary-line__label">
															{t(
																"goalkeeper_report.shot_map_summary.abbr_save_pct",
															)}
														</span>
														<span className="shot-map-summary-line__val">
															{savePercentageDisplay}
														</span>
													</span>
												</div>

												<div className="shot-map-insight">
													<div className="shot-map-insight-title">
														{t("goalkeeper_report.shot_map_insight.title")}
													</div>
													<div className="shot-map-insight-text">
														{shotMapInsight}
													</div>
												</div>

												<div className="shot-map-stats shot-map-stats--grid shot-map-stats--mini">
													<div className="shot-stat">
														<div className="shot-stat-label">
															{t(
																"goalkeeper_report.shot_map_stats.shots_faced",
															)}
														</div>
														<div className="shot-stat-value">
															{shotsOnTargetFacedDisplay}
														</div>
													</div>
													<div className="shot-stat">
														<div className="shot-stat-label">
															{t("goalkeeper_report.shot_map_stats.saves")}
														</div>
														<div className="shot-stat-value">
															{savesDisplay}
														</div>
													</div>
													<div className="shot-stat">
														<div className="shot-stat-label">
															{t(
																"goalkeeper_report.shot_map_stats.goals_conceded",
															)}
														</div>
														<div className="shot-stat-value">
															{goalsConcededDisplay}
														</div>
													</div>
													<div className="shot-stat">
														<div className="shot-stat-label">
															{t(
																"goalkeeper_report.shot_map_stats.saves_percentage",
															)}
														</div>
														<div className="shot-stat-value">
															{savePercentageDisplay}
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.final_summary")}
							</div>
							<div className="gk-section-content">
								<div className="row no-margin parameters-area">
									<div className="col-12">
										<div className="row">
											<div className="col-12 col-lg-6 col-xl-3 parameter-container">
												<div className="field-label-container">
													<div className="field-label">
														{t("goalkeeper_report.final_summary.offense_total")}
													</div>
												</div>
												<div className="parameter-total">
													{reportData?.totalOffensiveGameOk +
														reportData?.totalOffensiveGameFailed}
												</div>
											</div>
											<div className="col-12 col-lg-6 col-xl-3 parameter-container">
												<div className="field-label-container">
													<div className="field-label">
														{t("goalkeeper_report.final_summary.defense_total")}
													</div>
												</div>
												<div className="parameter-total">
													{reportData?.totalDefensiveGameOk +
														reportData?.totalDefensiveGameFailed}
												</div>
											</div>
											<div className="col-12 col-lg-6 col-xl-3 parameter-container">
												<div className="field-label-container">
													<div className="field-label">
														{t("goalkeeper_report.final_summary.volume_total")}
													</div>
												</div>
												<div className="parameter-total">
													{reportData?.totalOffensiveGameOk +
														reportData?.totalOffensiveGameFailed +
														reportData?.totalDefensiveGameOk +
														reportData?.totalDefensiveGameFailed}
												</div>
											</div>
											<div className="col-12 col-lg-6 col-xl-3 parameter-container">
												<div className="field-label-container">
													<div className="field-label">
														{t(
															"goalkeeper_report.final_summary.corners_conceded_total",
														)}
													</div>
												</div>
												<div className="parameter-total">
													{reportData?.totalCorners}
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.offensive_phase")}
							</div>
							<div className="gk-section-content">
								<div className="row parameters-area">
									<div className="col-12 col-xl-3">
										<div className="parameters-image">
											<img
												src={require("@/assets/images/manager/pass-soccer-ball.png")}
												alt=""
											/>
										</div>
									</div>
									<div className="col-12 col-xl-9">
										{getParameterRow(
											t("goalkeeper_report.offensive.short_pass"),
											"shortPassOk",
											"shortPassFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.offensive.long_throw"),
											"longMidThrowOk",
											"longMidThrowFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.offensive.restart_with_hands"),
											"restartWithHandsOk",
											"restartWithHandsFailed",
										)}
										{getParameterRow(
											t(
												"goalkeeper_report.offensive.restart_with_fly_remittal",
											),
											"remittalOnTheFlyOk",
											"remittalOnTheFlyFailed",
										)}
										{getParameterRow(
											t(
												"goalkeeper_report.offensive.positive_transition_outside",
											),
											"positiveTransitionOutsideOk",
											"positiveTransitionOutsideFailed",
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.defensive_phase")}
							</div>
							<div className="gk-section-content">
								<div className="row parameters-area">
									<div className="col-12 col-xl-3">
										<div className="parameters-image">
											<img
												src={require("@/assets/images/manager/hockey-goalpost.png")}
												alt=""
											/>
										</div>
									</div>
									<div className="col-12 col-xl-9">
										{getParameterRow(
											t("goalkeeper_report.defensive.save_grip"),
											"saveGripOk",
											"saveGripFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.defensive.save_rebound_deviation"),
											"saveReboundOk",
											"saveReboundFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.defensive.high_claim"),
											"highClaimOk",
											"highClaimFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.defensive.high_punch"),
											"highPunchOk",
											"highPunchFailed",
										)}
										{getParameterRow(
											t("goalkeeper_report.defensive.low_dive"),
											"lowDiveOk",
											"lowDiveFailed",
										)}
										{getParameterRow(
											t(
												"goalkeeper_report.defensive.depth_coverage_inside_area",
											),
											"depthCoverageInsideOk",
											"depthCoverageInsideFailed",
										)}
										{getParameterRow(
											t(
												"goalkeeper_report.defensive.depth_coverage_outside_area",
											),
											"depthCoverageOutsideOk",
											"depthCoverageOutsideFailed",
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.set_piece_corners_out")}
							</div>
							<div className="gk-section-content">
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={cornersOutConfiguration} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.set_piece_corners_in")}
							</div>
							<div className="gk-section-content">
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={cornersInConfiguration} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<div className="gk-light-section">
							<div className="gk-section-title">
								{t("goalkeeper_report.sections.set_piece_metrics")}
							</div>
							<div className="gk-section-content">
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={statsConfiguration} />
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<VLFCard
							openable={false}
							title={t("goalkeeper_report.coach_notes.free_notes")}
							content={
								<div className="coach-notes-content">
									{/* <div className="coach-notes-section">
										<div className="coach-notes-title">
											{t("goalkeeper_report.view_fields.general_opinion")}
										</div>
										<div className="coach-notes-highlight">
											{reportData?.generalOpinion?.length
												? reportData?.generalOpinion
												: naPlaceholder}
										</div>
									</div> */}

									{/* <div className="coach-notes-section">
										<div className="coach-notes-title">
											{t("goalkeeper_report.report_info.conceded_goals_description")}
										</div>

										{concededGoalDescriptions?.length ? (
											<ol className="coach-notes-goals-list">
												{concededGoalDescriptions.map((text, idx) => (
													<li key={idx} className="coach-notes-goal-item">
														{text}
													</li>
												))}
											</ol>
										) : (
											<div className="coach-notes-empty-state">
												{t(
													"goalkeeper_report.report_info.no_conceded_goals_description",
												)}
											</div>
										)}
									</div> */}

									<div className="coach-notes-section">
										{/* <div className="coach-notes-title">
											{t("goalkeeper_report.coach_notes.free_notes")}
										</div> */}
										<div className="coach-notes-highlight coach-notes-highlight--free">
											{reportData?.reportText?.length
												? reportData?.reportText
												: t("goalkeeper_report.coach_notes.no_free_notes")}
										</div>
									</div>
								</div>
							}
						/>
					</div>
				</div>
				{/* <div className="row no-margin mt-2">
					<div className="col-sm-12 no-padding">
						<VLFCard
							title="Dettagli"
							content={
								<div className="row">
									<div className="col-sm-12">
										<ViewField configuration={reportDetailsConfiguration} />
									</div>
								</div>
							}
						/>
					</div>
				</div> */}
			</div>
			<VLFModal
				open={openHandleConfirm}
				title={t("goalkeeper_report.modal.delete_title")}
				content={
					<div className="mt-2">
						{t("goalkeeper_report.modal.delete_question")}
					</div>
				}
				confirm={() => deleteReport()}
				close={() => setOpenHandleConfirm(false)}
				confirmLabel={t("goalkeeper_report.modal.confirm")}
			/>
		</Page>
	);
}

export default Valuation;
